import { Pause, Play } from "lucide-react"
import { useEffect, useId, useRef, useState } from "react"
import { cn } from "@/lib/utils"

type AudioPreviewPlayerProps = {
  src: string
  className?: string
}

/** Глобальное «играет только один»: слушатели останавливаются, если detail — не их id. */
export const AUDIO_PREVIEW_STOP_EVENT = "frameriver:audio-preview-stop"
const STOP_EVENT = AUDIO_PREVIEW_STOP_EVENT

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00"
  const total = Math.floor(seconds)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${s.toString().padStart(2, "0")}`
}

export function AudioPreviewPlayer({ src, className }: AudioPreviewPlayerProps) {
  const playerId = useId()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const onStopOthers = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail
      if (detail === playerId) return
      const audio = audioRef.current
      if (!audio) return
      audio.pause()
      setPlaying(false)
    }
    window.addEventListener(STOP_EVENT, onStopOthers)
    return () => window.removeEventListener(STOP_EVENT, onStopOthers)
  }, [playerId])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    audio.currentTime = 0
    setPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [src])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      return
    }
    window.dispatchEvent(new CustomEvent(STOP_EVENT, { detail: playerId }))
    void audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false))
  }

  const onSeek = (value: number) => {
    const audio = audioRef.current
    if (!audio || !Number.isFinite(duration) || duration <= 0) return
    const next = Math.min(Math.max(value, 0), duration)
    audio.currentTime = next
    setCurrentTime(next)
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      className={cn(
        "flex h-8 min-w-0 items-center gap-2 rounded-lg border border-ui-border bg-ui-surface-raised px-1.5",
        className,
      )}
    >
      <audio
        ref={audioRef}
        preload="none"
        src={src}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onDurationChange={() => setDuration(audioRef.current?.duration ?? 0)}
        onEnded={() => {
          setPlaying(false)
          setCurrentTime(0)
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <button
        type="button"
        onClick={togglePlay}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ui-text-primary transition-colors hover:bg-ui-accent/20 hover:text-ui-accent-text"
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? (
          <Pause className="h-3.5 w-3.5" fill="currentColor" />
        ) : (
          <Play className="h-3.5 w-3.5" fill="currentColor" />
        )}
      </button>
      <div className="relative min-w-0 flex-1">
        <div className="h-1 overflow-hidden rounded-full bg-ui-surface-sunken">
          <div className="h-full rounded-full bg-ui-accent" style={{ width: `${progress}%` }} />
        </div>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={currentTime}
          disabled={!duration}
          onChange={(e) => onSeek(Number(e.target.value))}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-default"
          aria-label="Seek"
        />
      </div>
      <span className="shrink-0 text-[10px] text-ui-text-muted tabular-nums">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  )
}
